/**
 * Leave-type policy form schema (D-12 + Pitfall H6).
 *
 * **Important backend-schema constraint** (Pitfall H6): `LeaveTypeView` is flat —
 * there are NO tenure-tier, once-in-lifetime, or Hajj-eligibility fields. Jordan
 * labor-law rules (14/21 day annual tier, Hajj tenure gate) are enforced
 * server-side. The UI form discriminates on `code` for CONDITIONAL FIELD
 * RENDERING only; all values map to existing `LeaveTypeView` fields:
 *
 *   - `ANNUAL`: `minDaysPerRequest` = 14 (under-5-years tier),
 *               `maxDaysPerRequest` = 21 (5+-years tier)
 *   - `HAJJ`:   standard policy fields; "once-in-lifetime" is an info banner
 *               sourced from `hr-domain/leaves/eligibility.ts`, not a form field
 *   - Others:   standard policy fields
 *
 * **Immutability on update** (Open Question §3): `UpdateLeaveTypeRequest` excludes
 * `paid`, `accrualPolicy`, `code`. This schema validates the CREATE shape; the
 * PATCH shape is a subset of these fields and lives implicitly in the
 * `UpdateLeaveTypeRequest` generated type.
 */
import { z } from "zod";

const basePolicyShape = {
  nameEn: z.string().min(1).max(60),
  nameAr: z.string().min(1).max(60),
  // `paid` is REQUIRED on CreateLeaveTypeRequest. It was previously absent from
  // this schema, so RHF stripped it from the submit body and every type created
  // through the drawer silently persisted as UNPAID. (Excluded from
  // UpdateLeaveTypeRequest, so the edit submit strips it back out.)
  paid: z.boolean(),
  // accrualPolicy is required on create (excluded from UpdateLeaveTypeRequest, so
  // the edit submit strips it). approvalChain is required on BOTH create and
  // update — a JSON-array string of ordered role codes (be-313).
  accrualPolicy: z.enum(["NONE", "MONTHLY_PRORATED", "ANNIVERSARY_GRANT", "ANNUAL_FISCAL_GRANT"]),
  approvalChain: z.string().min(1),
  accrualDaysPerYear: z.number().min(0).max(365),
  maxBalanceDays: z.number().min(0).max(365).nullable(),
  minDaysPerRequest: z.number().min(0).max(365),
  maxDaysPerRequest: z.number().min(0).max(365),
  attachmentRequired: z.boolean(),
  carryoverPolicy: z.enum(["NONE", "CAPPED", "ALL"]),
  carryoverCapDays: z.number().min(0).max(365).nullable(),
  // dayBasis is required on the wire's view (defaults WORKING on
  // create); genderRestriction is nullable — null means the type applies to
  // everyone. The form always carries both; the submit maps null → the
  // clearGenderRestriction flag on update.
  dayBasis: z.enum(["WORKING", "CALENDAR"]),
  genderRestriction: z.enum(["MALE", "FEMALE"]).nullable(),
  // Leave by the hour. Without these two the resolver strips them from the
  // submit values. A null cap means no limit beyond the day's shift. Its
  // bounds are checked below, only while hourly is on (the field is hidden
  // otherwise, and an unseen error would silently block Save).
  allowHourly: z.boolean().default(false),
  maxHoursPerRequest: z.number().nullable().default(null),
} as const;

/** The hourly cap: 0.25 to 24, in quarter hours (stored with two decimals). */
function hourCapInRange(hours: number): boolean {
  return hours >= 0.25 && hours <= 24 && Math.abs(hours * 4 - Math.round(hours * 4)) < 1e-9;
}

/**
 * Known Jordan-seeded codes (be-011 auto-seeds 8 types). Tenant-added codes
 * fall through to the `fallbackLeaveTypePolicySchema` below.
 */
export const knownLeaveTypePolicySchema = z.discriminatedUnion("code", [
  z.object({ code: z.literal("ANNUAL"), ...basePolicyShape }),
  z.object({ code: z.literal("HAJJ"), ...basePolicyShape }),
  z.object({ code: z.literal("SICK"), ...basePolicyShape }),
  z.object({ code: z.literal("MATERNITY"), ...basePolicyShape }),
  z.object({ code: z.literal("PATERNITY"), ...basePolicyShape }),
  z.object({ code: z.literal("BEREAVEMENT"), ...basePolicyShape }),
  z.object({ code: z.literal("UNPAID"), ...basePolicyShape }),
  z.object({ code: z.literal("CASUAL"), ...basePolicyShape }),
]);

/** Known Jordan codes for the tenant-added fallback refinement. */
const JORDAN_SEEDED_CODES = [
  "ANNUAL",
  "HAJJ",
  "SICK",
  "MATERNITY",
  "PATERNITY",
  "BEREAVEMENT",
  "UNPAID",
  "CASUAL",
] as const;

/**
 * Fallback for tenant-added types — the code must NOT collide with a reserved
 * Jordan-seeded code (those go through `knownLeaveTypePolicySchema`).
 */
export const fallbackLeaveTypePolicySchema = z.object({
  code: z
    .string()
    .min(1)
    .max(40)
    .refine((c) => !(JORDAN_SEEDED_CODES as readonly string[]).includes(c), {
      message: "tenant-code-only",
    }),
  ...basePolicyShape,
});

/**
 * Public policy schema — consumer chooses branch at runtime via union semantics
 * (known discriminated union parsed first for better error narrowing, fallback
 * permissive object for tenant-added codes).
 */
export const leaveTypePolicySchema = z
  .union([knownLeaveTypePolicySchema, fallbackLeaveTypePolicySchema])
  // Cross-field guard: a minimum-per-request larger than the maximum is
  // internally inconsistent. Both fields are required on every branch, so the
  // parsed value always carries them. Inline message matches this file's style.
  .superRefine((data, ctx) => {
    if (data.minDaysPerRequest > data.maxDaysPerRequest) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "min-days-exceeds-max",
        path: ["maxDaysPerRequest"],
      });
    }
    if (
      data.allowHourly &&
      data.maxHoursPerRequest !== null &&
      !hourCapInRange(data.maxHoursPerRequest)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "leaves:policy.max_hours_range",
        path: ["maxHoursPerRequest"],
      });
    }
  });

export type LeaveTypePolicyFormValues = z.infer<typeof leaveTypePolicySchema>;
